import { FormControl, FormGroup, ɵFormGroupValue } from '@angular/forms';
import { ResolveData } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators';
import {
    AccountsReceivableLedgerTypeEnum,
    ARLTypeEnumNames,
    financialRound,
} from '../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { SurchargeRateEntity } from '../../../dave-data-module/entities/surcharge-rate.entity';
import { SurchargeRateResolver } from '../../../dave-data-module/guards/surcharge-rate.resolver';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { State } from '../../../dave-data-module/State';
import { SURCHARGE_RATE_KEY } from '../../../dave-data-module/State/reducers/surcharge-rate.reducer';
import { getSurchargeRateByArlType, getSurchargeRateFetched } from '../../../dave-data-module/State/selectors/surcharge-rate.selector';
import { isNotNullOrUndefined, MathRound } from '../../../helper/helper';
import { arlTableData } from './arl-calculation.helper';
import { calculationArlForm } from './calculation-arl-form-data.service';
import { calculationArlTemplateForm } from './calculation-arl-template-form-data.service';
import { arlIsAmountRelevant } from './arl-calculation.helper';
import {
    getAccountsReceivableLedgerDictionary, getQuantityTypeDictionary,
    getQuantityTypes,
} from '../../../dave-data-module/State/selectors/accounting.selector';
// import { boolean, number } from 'mathjs';
// import { ArlFormDataService } from './arl-form-data.service';
// import { inject } from '@angular/core';
// import { QuantityTypeEntity } from '../../../dave-data-module/entities/quantity-type.entity';

export class ArlCalculationSurcharge<T extends calculationArlForm | calculationArlTemplateForm> {
    public static readonly RequiredResolvers: ResolveData = {
        [SURCHARGE_RATE_KEY]: SurchargeRateResolver,
    };
    public formInitialised$ = new BehaviorSubject(false);

    public form = new FormGroup({
        usePercent: new FormControl<boolean>(false),
        percent: new FormControl<number>(null),
        decimal: new FormControl<number>(null),
    });
    private arlsByType$: Observable<{ formValue: ɵFormGroupValue<T>; pieceCostAmountWithoutTax$: Observable<number> }[]>;
    private arlsByTypeForIndividuallySurcharge$: Observable<{ formValue: ɵFormGroupValue<T>; pieceCostAmountWithoutTax$: Observable<number> }[]>;
    private arlsByTypeForSurchargeByType$: Observable<{ formValue: ɵFormGroupValue<T>; pieceCostAmountWithoutTax$: Observable<number> }[]>;

    public amountSum$: Observable<number>;
    public amountSumForIndividuallySurcharge$: Observable<number>;
    public amountSumWithZuschl$: Observable<number>;
    public surchargeAddition$: Observable<number>;
    public amountSumWithZuschlForIndividuallySurcharge$: Observable<number>;
    public name: string;
    public amountGZ$: Observable<number>;
    public overrideMode$: Observable<boolean>;

    // private defaultSurchargeRate$: Observable<SurchargeRateEntity | null | undefined>;
    // private arlFormDataService = inject(ArlFormDataService);


    /**
     *
     * @param type
     * @param tableData
     * @param preCharge$ 0.01 = 1%
     * @param defaultSurchargeRate$
     */
    constructor(
        public type: AccountsReceivableLedgerTypeEnum,
        tableData: Observable<arlTableData<any>[]>,
        preCharge$: Observable<number>,
        private defaultSurchargeRate$: Observable<number | null | undefined>,
        public overrideZS$: Observable<number | null>,
    ) {
        this.name = ARLTypeEnumNames.get(type);
        // this.defaultSurchargeRate$ = getFetched$(store, getSurchargeRateFetched, getSurchargeRateByArlType({ arlType: type }));
        this.arlsByType$ = tableData.pipe(
            switchMap((data): Observable<{formValue: ɵFormGroupValue<T>; pieceCostAmountWithoutTax$: Observable<number>;}[]> =>
                    data.length
                        ? combineLatest(
                              data.map<Observable<{formValue: ɵFormGroupValue<T>; pieceCostAmountWithoutTax$: Observable<number>;}>>((d) =>
                                  d.form.valueChanges.pipe(
                                      startWith(null),
                                      map(() => {
                                          return {
                                              formValue: d.form.getRawValue(),
                                              pieceCostAmountWithoutTax$: d.pieceCostAmountWithoutTax$,
                                              // costAmountWithoutTax$: d.costAmountWithoutTax$,
                                          };
                                      }),
                                  ),
                              ),
                          )
                        : of([] as {formValue: ɵFormGroupValue<T>; pieceCostAmountWithoutTax$: Observable<number>;}[]),
            ),
            map((val) => val.filter((v) => v.formValue.Type === type)),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.overrideMode$ = combineLatest([
            overrideZS$,
            this.arlsByType$.pipe(map(arl => arl.some(a => isNotNullOrUndefined(a.formValue.SurchargeOverride)))),
        ]).pipe(map(([ov, lockedArl]) => isNotNullOrUndefined(ov) || lockedArl), distinctUntilChanged());

        this.arlsByTypeForIndividuallySurcharge$ = this.arlsByType$.pipe(map(arls => arls.filter(arl => arl.formValue.IndividuallyCostCalculation)));
        this.arlsByTypeForSurchargeByType$ = this.arlsByType$.pipe(map(arls => arls.filter(arl => !arl.formValue.IndividuallyCostCalculation)));
        function arlsToCostAmounts$(values: {formValue: ɵFormGroupValue<T>, pieceCostAmountWithoutTax$: Observable<number>}[]): Observable<{cost: number, quantity: number}[]> {
            const arls = values.filter((v) => arlIsAmountRelevant({IsOptional: v.formValue.IsOptional, CrossedOut: v.formValue.CrossedOut}));
            if (arls.length) {
                return combineLatest(arls.map((v) => v.pieceCostAmountWithoutTax$.pipe(map((cost) => ({cost, quantity: v.formValue.Quantity})))));
            } else {
                return of([]);
            }
        }
        this.amountSum$ = this.arlsByTypeForSurchargeByType$.pipe(
            switchMap((values) => arlsToCostAmounts$(values)),
            map((values) => values.map(v => {
                // runden ? financialRound(v.cost * v.quantity)
                return v.cost * v.quantity;
            }).reduce((p, c) => p + c, 0)),
        );
        this.amountSumForIndividuallySurcharge$ = this.arlsByTypeForIndividuallySurcharge$.pipe(
            switchMap((values) => arlsToCostAmounts$(values)),
            map((values) => values.map(v => v.cost * v.quantity).reduce((p, c) => p + c, 0)),
        );
        this.amountSumWithZuschlForIndividuallySurcharge$ = this.arlsByTypeForIndividuallySurcharge$.pipe(map((values) => {
            const arls = values.filter((v) => arlIsAmountRelevant({IsOptional: v.formValue.IsOptional, CrossedOut: v.formValue.CrossedOut}));
            return arls.map<number>(arl => financialRound((arl.formValue.BaseCost || 0)*(arl.formValue.Quantity || 0))).reduce((p, c) => p + c, 0)
        }));
        this.amountSumWithZuschl$ = combineLatest([
            this.arlsByTypeForSurchargeByType$,
            this.form.valueChanges.pipe(
                startWith(null),
                map(() => this.form.value),
            ),
            preCharge$,
            this.formInitialised$.pipe(filter((v) => !!v)),
            overrideZS$,
        ]).pipe(
            switchMap(([amounts, formValue, preCharge, inited,  overrideZS]) => {
                const filteredARLS = amounts.filter(a => arlIsAmountRelevant({IsOptional: a.formValue.IsOptional, CrossedOut: a.formValue.CrossedOut}));
                if (filteredARLS.length === 0) {
                    return of(0);
                }

                return combineLatest(
                    filteredARLS.map<Observable<number>>((v) => v.pieceCostAmountWithoutTax$.pipe(map(cost => {
                        if (isNotNullOrUndefined(overrideZS)) {
                            return getAmountFromPieceAmount(overrideZS, cost, 0, v.formValue.Quantity);
                        }
                        return getAmountFromPieceAmount(formValue.percent, cost, preCharge, v.formValue.Quantity);
                    }))),
                ).pipe(map((v) => v.reduce((prev, curr) => prev + curr, 0)));
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
     this.surchargeAddition$ = combineLatest([
            this.amountSumWithZuschl$,
            this.amountSum$,
            this.form.valueChanges.pipe(startWith(this.form.value))
        ]).pipe(
            map(([withZuschl, amountSum, formValue]) =>
                financialRound(withZuschl - (amountSum + formValue.decimal))
            )
        );
        this.amountGZ$ = combineLatest([
            this.amountSum$,
            this.amountSumWithZuschl$
        ]).pipe(
            map(([amountSum, amountSumWithZuschl]) =>
                ((100 * amountSumWithZuschl) / amountSum) - 100
            )
        );
    }
    // private ArlCalculationSurchargeGetPositionAmount(cost: number, formValue: Partial<{
    //     usePercent: boolean
    //     percent: number
    //     decimal: number
    // }> ,preCharge: number) {
    //     const zuschlInPercent = (formValue.percent || 0) / 100;
    //     const amount = parseFloat(((cost || 0) + (cost || 0) * zuschlInPercent).toFixed(3)); // ?
    //     const pieceAmount = financialRound(amount + amount * (preCharge || 0))
    //     return financialRound(pieceAmount * (v.formValue.Quantity || 0));
    // }
    private _init(val: number) {
        val = val || 0;
        if (this.form.value.decimal !== val) {
            firstValueFrom(this.amountSum$).then((amount) => {
                this.setFormValue(false, amount, val, true, false);
                this.form.markAsPristine();
                this.formInitialised$.next(true);
            });
        } else {
            this.form.markAsPristine();
            this.formInitialised$.next(true);
        }
    }
    public init(val: number) {
            // console.log('init#', val);
        firstValueFrom(this.overrideZS$).then((overrideZS) => {
            if (isNotNullOrUndefined(overrideZS)) {
                firstValueFrom(this.amountSum$).then((amount) => {
                    this.setFormValue(true, amount, overrideZS, true, false);
                    this.form.markAsPristine();
                    this.formInitialised$.next(true);
                });
            } else {
                if (!isNotNullOrUndefined(val)) {
                    firstValueFrom(this.defaultSurchargeRate$).then((ds) => {
                        if (ds) {
                            firstValueFrom(this.amountSum$).then((amount) => {
                                this.setFormValue(true, amount, ds, true, false);
                                this.form.markAsPristine();
                                this.formInitialised$.next(true);
                            });
                        } else {
                            this._init(val);
                        }
                    });
                } else {
                    this._init(val);
                }
            }
        });
    }

    public setFormValue(usePercentValue: boolean, sumAmount: number, value: number, setPercentActive: boolean, markDirty = true) {
        //console.log(sumAmount);
        const round = (num: number) => MathRound(num * 100, true) / 100;
        let val;
        if (usePercentValue) {
            val = {
                usePercent: setPercentActive,
                percent: value,
                decimal: value && round(sumAmount * (1 + value / 100) - sumAmount),
            };
        } else {
            val = {
                usePercent: setPercentActive,
                percent: value && sumAmount && round((value / sumAmount) * 100),
                decimal: value,
            };
        }
        if (val.usePercent !== this.form.value.usePercent || val.percent !== this.form.value.percent || val.decimal !== this.form.value.decimal) {
            this.form.setValue(val);
            if (markDirty) {
                this.form.markAsDirty();
            }
        }
    }
    setPercent(value: number) {
        firstValueFrom(this.amountSum$).then((amount) => {
            this.setFormValue(true, amount, value || 0, true);
        });
    }
    settDecimal(value: number) {
        firstValueFrom(this.amountSum$).then((amount) => {
            this.setFormValue(false, amount, value || 0, false);
        });
    }
}

/**
 *
 * @param percent
 * @param cost
 * @param preCharge NOT PreChargePerType
 * @param quantity
 */
export function getAmountFromPieceAmount(percent: number, cost: number, preCharge: number, quantity: number) {
    const zuschlInPercent = (percent || 0) / 100;
    const amount = parseFloat(((cost || 0) + (cost || 0) * zuschlInPercent).toFixed(3)); // ?
    const pieceAmount = financialRound(amount + amount * (preCharge || 0))
    const ret = financialRound(pieceAmount * (quantity || 0));
    return ret;
}
